import TR from 'utils/TR';
import { Chart } from 'chart.js';
import Section from 'components/common/Section';
import DIRECTION_VERTICAL from 'constants/DIRECTION_VERTICAL';
import useRect from 'hooks/common/useRect';
import React from 'react';
import T from 'utils/T';
import ex from 'utils/ex';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';

import 'chartjs-adapter-luxon';
import useReportAgpGraph from 'hooks/communication/useReportAgpGraph';

const columns = Array.from({ length: 23 * 4 }).map((name, index) => `${Math.ceil(index / 4).toString().padStart(2, '0')}${((index % 4) * 15).toString().padStart(2, '0')}`);
const config = (hash) => ({
  type: 'line',
  data: {
    labels: columns,
    datasets: [
      {
        data: columns.map((name, index) => {
          return hash[name]?.bld_sugr_5per || 0;
        }),
        pointRadius: 0,
        borderWidth: 0,
        cubicInterpolationMode: 'monotone',
        tension: 0.4,
      }, {
        data: columns.map((name, index) => {
          return hash[name]?.bld_sugr_95per || 0;
        }),
        pointRadius: 0,
        borderWidth: 0,
        fill: '-1',
        cubicInterpolationMode: 'monotone',
        backgroundColor: '#80ff8040',
        tension: 0.4,
      }, {
        data: columns.map((name, index) => {
          return hash[name]?.bld_sugr_25per || 0;
        }),
        pointRadius: 0,
        borderWidth: 0,
        cubicInterpolationMode: 'monotone',
        tension: 0.4,
        order: 3,
      }, {
        data: columns.map((name, index) => {
          return hash[name]?.bld_sugr_75per || 0;
        }),
        pointRadius: 0,
        borderWidth: 0,
        fill: '-1',
        cubicInterpolationMode: 'monotone',
        backgroundColor: '#80ff8080',
        tension: 0.4,
        order: 2,
      }, {
        data: columns.map((name, index) => {
          return hash[name]?.bld_sugr_50per || 0;
        }),
        pointRadius: 0,
        borderWidth: 1,
        cubicInterpolationMode: 'monotone',
        borderColor: '#0000ff',
        tension: 0.1,
        order: 1,
      }
    ]
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        display: true,
        ticks: {
          color: '#000000',
          callback: (value) => {
            return value % 6 ? '' : '';
          },
          stepSize: 24,
        },
        title: {
          display: false,
        },
        grid: {
          drawTicks: false,
        },
      },
      y: {
        display: true,
        afterTickToLabelConversion: (scaleInstance) => {
          const ticks = scaleInstance.ticks;
          const newTicks = ticks?.filter((tick) => {
            switch (tick.value) {
              case 0: return true;
              case 54: return true;
              case 70: return true;
              case 180: return true;
              case 250: return true;
              case 350: return true;
              default: return false;
            }
          });
          scaleInstance.ticks = newTicks;
        },
        title: {
          display: false,
        },
        min: 0,
        max: 350,
        ticks: {
          callback: (value) => {
            switch (value) {
              case 0:
              case 54:
              case 70:
              case 180:
              case 250:
              case 350:
                return '';
              default:
                return '';
            }
          },
          stepSize: 1,
        },
        grid: {
          drawTicks: false,
        },
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
  },
  plugins: [{
    afterDatasetsDraw: (chart) => {
      // let ctx = chart.ctx;
      // const xAxis = chart.scales.x;
      // const yAxis = chart.scales.y;
      // if (!xAxis) return;
      // if (!yAxis) return;

      // const high = yAxis.getPixelForValue(180);
      // const low = yAxis.getPixelForValue(70);
      // const zero = yAxis.getPixelForValue(0);

      // ctx.save();
      // ctx.globalCompositeOperation = 'source-atop';

      // ctx.globalAlpha = 0.5;
      // ctx.fillStyle = '#ff0000';
      // ctx.fillRect(xAxis.left, yAxis.top, xAxis.right - xAxis.left, high);

      // ctx.fillStyle = '#00ff00';
      // ctx.fillRect(xAxis.left, high, xAxis.right - xAxis.left, low);

      // ctx.fillStyle = '#0000ff';
      // ctx.fillRect(xAxis.left, low, xAxis.right - xAxis.left, zero);

      // ctx.restore();
    },
  }],
});

export default function PatternChartBox(props) {
  const {
    search_dv,
    search_key,
    ...rest
  } = props || {};

  const period_start = Recoil.useRecoilValue(AppStore.assets.period_start);
  const period_end = Recoil.useRecoilValue(AppStore.assets.period_end);
  const [agp, setAgp] = React.useState([]);
  const {
    requestReportAgpGraph,
  } = useReportAgpGraph();

  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  React.useEffect(() => {
    if (!patient_no) return;
    requestReportAgpGraph({
      patient_no,
      search_start_time: period_start,
      search_end_time: period_end,
      search_dv,
      search_key,
    }, (success, data) => {
      if (success) {
        setAgp(() => data.agp_graph_list);
      }
    });
  }, [period_end, period_start, search_dv, search_key, setAgp, patient_no, requestReportAgpGraph]);

  const ref = React.useRef();
  const rect = useRect(ref);
  const canvasRef = React.useRef();
  const chartRef = React.useRef();

  const hash = React.useMemo(() => agp.reduce((a, c) => ({
    ...a,
    [c.time]: c,
  }), {}), [agp]);

  React.useEffect(() => {
    const chart = new Chart(canvasRef.current, config(hash));
    chartRef.current = chart;
    return () => {
      chart.destroy();
      delete chart.config;
    };
  }, [hash]);
  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    const frameId = requestAnimationFrame(() => {
      chartRef.current.update();
    });
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [rect]);
  return (
    <div {...ex('PatternChartBox', rest)}>
      <div {...ex('Chart', { ref })}>
        <canvas
          {...{
            ref: canvasRef,
            width: rect.width,
            height: rect.height,
          }}
        />
      </div>
    </div>
  );
}

